/** OPTION D — Customer Workspace. Feels tenant-owned, ONE stays as a footer. */
import { ArrowRight, Loader2, ChevronLeft } from "lucide-react";
import { useLoginAuth } from "./useLoginAuth";

export default function OptionD() {
  const a = useLoginAuth();
  const tenantName = a.tenantHint?.tenant_name;   // safe: null unless server chose to reveal
  const initial = tenantName ? tenantName.trim()[0].toUpperCase() : "";

  return (
    <div className="min-h-screen bg-white text-[color:var(--st-text)] flex flex-col" data-testid="login-option-d">
      <header className="border-b border-[color:var(--st-border)]">
        <div className="max-w-5xl mx-auto flex items-center h-14 px-8">
          <div className="text-[13px] font-semibold tracking-[0.14em] text-[color:var(--st-text)]">SUNTRONIX ONE</div>
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-8 py-14">
        <div className="w-full max-w-[440px]">
          {a.step === "credentials" && tenantName && (
            <div className="mb-8 flex items-center gap-3">
              <div className="h-11 w-11 rounded-md bg-[color:var(--st-accent-soft)] text-[color:var(--st-accent)] grid place-items-center font-heading font-semibold text-lg">
                {initial}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-muted)]">Signing in to</div>
                <div className="text-lg font-heading font-semibold text-[color:var(--st-text)] leading-tight">{tenantName}</div>
              </div>
            </div>
          )}

          {a.step === "email" ? (
            <>
              <h1 className="font-heading text-[24px] font-medium tracking-[-0.01em]">
                Welcome to your technology workspace.
              </h1>
              <p className="mt-2 text-[13px] text-[color:var(--st-text-muted)]">
                Enter your work email to continue.
              </p>
              <form onSubmit={a.continueEmail} className="mt-8 space-y-4">
                <Field label="Work email" value={a.email} onChange={a.setEmail} type="email" required autoFocus placeholder="john@abcglobal.com" />
                {a.error && <ErrorMsg msg={a.error} />}
                <button type="submit" disabled={a.loading || !a.email} className="w-full h-11 rounded-md bg-[color:var(--st-text)] text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="d-continue">
                  {a.loading ? <Loader2 className="h-4 w-4 animate-spin"/> : (<>Continue <ArrowRight className="h-4 w-4" strokeWidth={1.6}/></>)}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="text-[11px] uppercase tracking-[0.14em] text-[color:var(--st-text-muted)]">Welcome back</div>
              <h1 className="font-heading text-[22px] font-medium mt-1">Sign in to continue</h1>
              <form onSubmit={a.submitPassword} className="mt-8 space-y-4">
                <button type="button" onClick={a.backToEmail} className="text-[12px] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] inline-flex items-center gap-1">
                  <ChevronLeft className="h-3.5 w-3.5" /> Use a different email
                </button>
                {a.tenantHint?.sso_enabled && a.tenantHint?.sso_url && (
                  <a href={a.tenantHint.sso_url} className="w-full h-11 rounded-md border border-[color:var(--st-border)] bg-white text-[13px] font-medium inline-flex items-center justify-center hover:bg-[color:var(--st-bg)]">
                    Continue with Microsoft
                  </a>
                )}
                <Field label="Password" value={a.password} onChange={a.setPassword} type="password" required autoFocus />
                {a.error && <ErrorMsg msg={a.error} />}
                <button type="submit" disabled={a.loading} className="w-full h-11 rounded-md bg-[color:var(--st-text)] text-white font-medium disabled:opacity-50 inline-flex items-center justify-center">
                  {a.loading ? <Loader2 className="h-4 w-4 animate-spin"/> : "Sign in"}
                </button>
              </form>
            </>
          )}
          <div className="mt-14 text-center text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-subtle)]">
            Powered by SUNTRONIX ONE
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({ label, value, onChange, ...p }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.14em] text-[color:var(--st-text-muted)]">{label}</span>
      <input {...p} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full h-11 px-3 rounded-md border border-[color:var(--st-border)] bg-white text-[15px] focus:outline-none focus:border-[color:var(--st-text)] focus:ring-2 focus:ring-[color:var(--st-text)]/10" />
    </label>
  );
}
function ErrorMsg({ msg }) { return <div role="alert" className="text-[12px] text-[color:var(--st-critical)] bg-[color:var(--st-critical-soft)] border border-[color:var(--st-critical-soft)] rounded-md px-3 py-2">{msg}</div>; }
